import axios from 'axios';

import Creators from './actions';
import { categoryOperations } from '../categories';
import { segmentOperations } from '../segments';
import { brandOperations } from '../brands';
import { entitySearchServiceOperations } from '../entitySearchService';
import { userOperations } from '../user';
import { calculateNewQueryParams } from './selectors';
import { addHashIdToUserConfig } from '../user/selectors';
import { Dispatch } from 'react';
import ReduxStore from 'src/types/store/reduxStore';
import { getApiHost } from 'src/utils/ssoReferrer';

export const { isUpdatingRedux, setAppName, setTargetUrl, receiveAppConfig, requestConfig, setQueryParams } = Creators;

/**
 * App config fetcher.
 *
 * Tries V2 first (cookies set by v2IssueToken), falls back to V1
 * (cookies set by LoginExternalAuth) if V2 returns no session. The
 * endpoint that returns a valid session wins — so the same fetchConfig
 * works whether the user came in via V1 or V2 auth, without needing
 * a cross-domain hint about which flow they used.
 *
 * Does NOT touch the auth-version signal. That is owned by the API
 * (server-set `sl-auth-version` cookie on session issue / clear on logout)
 * — a transient V2 failure here must not be interpreted as a v2-to-v1
 * downgrade, which would disable V2-only behavior (useDivisionCheck
 * listener, DivisionDropdown, etc.) on the next render.
 */
export const fetchConfig = () => async (dispatch: Dispatch<{ type: string }>) => {
  dispatch(requestConfig());
  const apiHost = getApiHost();

  try {
    const v2Response = await axios.post(`https://${apiHost}/api/auth/v2/config`, {}, { withCredentials: true });
    if (v2Response.data?.session?.sessionId) {
      const responseWithHashId = addHashIdToUserConfig(v2Response);
      return [dispatch(receiveAppConfig(v2Response)), dispatch(userOperations.receiveUserConfig(responseWithHashId))];
    }
  } catch {
    /* fall through to V1 */
  }

  const v1Response = await axios.post(`https://${apiHost}/api/user/GetConfig`, {}, { withCredentials: true });
  const responseWithHashId = addHashIdToUserConfig(v1Response);
  return [dispatch(receiveAppConfig(v1Response)), dispatch(userOperations.receiveUserConfig(responseWithHashId))];
};

export const clearReduxState = () => (dispatch: Dispatch<{ type: string }>) => {
  //  Clear entity but the the clientSideBarCard has to be redone cause it is remounting
  dispatch(categoryOperations.clearCategories());
  dispatch(segmentOperations.clearAllSavedSearch());
  dispatch(brandOperations.clearBrandsFollowing());
  dispatch(entitySearchServiceOperations.clearEntitySearchService());
  dispatch(userOperations.clearUserConfig());
};

export const updateQueryParams =
  (
    app: ReduxStore['app'],
    retailer: Pick<ReduxStore['retailer'], 'id'>,
    mainTimePeriod: ReduxStore['mainTimePeriod'],
    comparisonTimePeriod: ReduxStore['comparisonTimePeriod'],
    additionalParams = {},
    parentPlatform = { id: null }
  ) =>
  (dispatch: Dispatch<{ type: string; params: any }>) => {
    return new Promise((resolve) => {
      const newQueryParams = calculateNewQueryParams(
        app,
        retailer,
        mainTimePeriod,
        comparisonTimePeriod,
        additionalParams,
        parentPlatform
      );
      resolve(dispatch(setQueryParams(newQueryParams)));
    });
  };
